import React from 'react'
import axios from 'axios';
import {
    Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle,
    Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select,
    TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { useRefresh } from 'react-admin';
import { OneMealService } from '../../services/OneMealService';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from './CustomSnackBar';
import HttpClient from '../../HttpClient';
import moment from 'moment';

const MODE_OF_TRAVEL_OPTIONS = ['Rapido', 'Ola', 'Uber', 'Self Vehicle', 'Local Transport'];

interface ConveyanceLeg {
    conveyanceAmount: number | undefined;
    modeOfTravel: string;
    photoUrl: string;
    uploading: boolean;
}

const defaultLeg = (): ConveyanceLeg => ({
    conveyanceAmount: undefined,
    modeOfTravel: '',
    photoUrl: '',
    uploading: false,
});

interface InputProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    data: any;
}

const RequestCookConveyanceDialog = (props: InputProps) => {
    const [tripType, setTripType] = React.useState<'oneWay' | 'bothWay'>('oneWay');
    const [toVisit, setToVisit] = React.useState<ConveyanceLeg>(defaultLeg());
    const [fromVisit, setFromVisit] = React.useState<ConveyanceLeg>(defaultLeg());
    const [isResubmitted, setIsResubmitted] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const refresh = useRefresh();
    const oneMealService = new OneMealService();

    const uploadPhoto = async (
        file: File,
        setter: React.Dispatch<React.SetStateAction<ConveyanceLeg>>
    ) => {
        const extension = file.name.split('.').pop();
        const timestamp = moment().format('DDMMYYYYHHmmss');
        const fileName = `conveyance_${timestamp}.${extension}`;

        setter((prev) => ({ ...prev, uploading: true }));
        try {
            const res = await HttpClient.get('uploadImage/getSignedUrl', {
                params: { directory: 'reimbursementProofs', fileName },
            });
            const { putUrl, getUrl } = res.data;
            await axios.put(putUrl, file);
            setter((prev) => ({ ...prev, photoUrl: getUrl, uploading: false }));
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Photo uploaded successfully',
                type: CustomSnackbarTypes.SUCCESS,
            });
        } catch {
            setter((prev) => ({ ...prev, uploading: false }));
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Photo upload failed. Please try again.',
                type: CustomSnackbarTypes.ERROR,
            });
        }
    };

    const handleToVisitAmountChange = (val: string) =>
        setToVisit((prev) => ({ ...prev, conveyanceAmount: val ? parseInt(val) : undefined }));

    const handleToVisitModeChange = (val: string) =>
        setToVisit((prev) => ({ ...prev, modeOfTravel: val }));

    const handleFromVisitAmountChange = (val: string) =>
        setFromVisit((prev) => ({ ...prev, conveyanceAmount: val ? parseInt(val) : undefined }));

    const handleFromVisitModeChange = (val: string) =>
        setFromVisit((prev) => ({ ...prev, modeOfTravel: val }));

    const handleSubmit = async () => {
        if (!toVisit.conveyanceAmount || !toVisit.modeOfTravel || !toVisit.photoUrl) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Please fill all fields and upload a photo for the To Work journey',
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }
        if (tripType === 'bothWay' && (!fromVisit.conveyanceAmount || !fromVisit.modeOfTravel || !fromVisit.photoUrl)) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Please fill all fields and upload a photo for the From Work journey',
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }

        const payload: any = {
            toVisit: {
                conveyanceAmount: toVisit.conveyanceAmount,
                modeOfTravel: toVisit.modeOfTravel,
                conveyanceProofScreenShot: toVisit.photoUrl,
            },
            isResubmitted,
        };

        if (tripType === 'bothWay') {
            payload.fromVisit = {
                conveyanceAmount: fromVisit.conveyanceAmount,
                modeOfTravel: fromVisit.modeOfTravel,
                conveyanceProofScreenShot: fromVisit.photoUrl,
            };
        }

        setSubmitting(true);
        try {
            await oneMealService.requestCookConveyance(parseInt(props?.data?.id), payload);
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Conveyance submitted successfully',
                type: CustomSnackbarTypes.SUCCESS,
            });
            refresh();
            props?.setOpen(false);
        } catch {
            // Error handling done by Axios interceptor via EventBus
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={props?.open} onClose={() => props?.setOpen(false)} scroll="paper" maxWidth="sm" fullWidth>
            <DialogTitle>Request Cook Conveyance</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pt: 1 }}>
                <ToggleButtonGroup
                    value={tripType}
                    exclusive
                    onChange={(_, val) => { if (val) setTripType(val); }}
                    fullWidth
                    size="small"
                >
                    <ToggleButton value="oneWay">One Way (To Work)</ToggleButton>
                    <ToggleButton value="bothWay">Both Ways</ToggleButton>
                </ToggleButtonGroup>

                {/* To Work */}
                <Box display="flex" flexDirection="column" gap="12px">
                    <Typography variant="subtitle1" fontWeight="bold">To Work Journey</Typography>
                    <TextField
                        label="Conveyance Amount (₹)"
                        fullWidth
                        type="number"
                        value={toVisit.conveyanceAmount ?? ''}
                        onChange={(e) => handleToVisitAmountChange(e.target.value)}
                        inputProps={{ min: 0 }}
                    />
                    <FormControl variant="filled" fullWidth>
                        <InputLabel>Mode of Travel</InputLabel>
                        <Select
                            value={toVisit.modeOfTravel}
                            label="Mode of Travel"
                            onChange={(e) => handleToVisitModeChange(e.target.value as string)}
                            fullWidth
                        >
                            {MODE_OF_TRAVEL_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box>
                        <Typography variant="body2" gutterBottom>Proof Photo *</Typography>
                        <input
                            accept="image/*"
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) uploadPhoto(file, setToVisit);
                            }}
                        />
                        {toVisit.uploading && <CircularProgress size={20} sx={{ ml: 1, verticalAlign: 'middle' }} />}
                        {toVisit.photoUrl && (
                            <img
                                src={toVisit.photoUrl}
                                alt="To Work Proof"
                                style={{ width: '100%', height: 'auto', marginTop: '8px', borderRadius: '8px', objectFit: 'cover' }}
                            />
                        )}
                    </Box>
                </Box>

                {/* From Work (optional, bothWay only) */}
                {tripType === 'bothWay' && (
                    <>
                        <Divider />
                        <Box display="flex" flexDirection="column" gap="12px">
                            <Typography variant="subtitle1" fontWeight="bold">From Work Journey</Typography>
                            <TextField
                                label="Conveyance Amount (₹)"
                                fullWidth
                                type="number"
                                value={fromVisit.conveyanceAmount ?? ''}
                                onChange={(e) => handleFromVisitAmountChange(e.target.value)}
                                inputProps={{ min: 0 }}
                            />
                            <FormControl variant="filled" fullWidth>
                                <InputLabel>Mode of Travel</InputLabel>
                                <Select
                                    value={fromVisit.modeOfTravel}
                                    label="Mode of Travel"
                                    onChange={(e) => handleFromVisitModeChange(e.target.value as string)}
                                    fullWidth
                                >
                                    {MODE_OF_TRAVEL_OPTIONS.map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box>
                                <Typography variant="body2" gutterBottom>Proof Photo *</Typography>
                                <input
                                    accept="image/*"
                                    type="file"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) uploadPhoto(file, setFromVisit);
                                    }}
                                />
                                {fromVisit.uploading && <CircularProgress size={20} sx={{ ml: 1, verticalAlign: 'middle' }} />}
                                {fromVisit.photoUrl && (
                                    <img
                                        src={fromVisit.photoUrl}
                                        alt="From Work Proof"
                                        style={{ width: '100%', height: 'auto', marginTop: '8px', borderRadius: '8px', objectFit: 'cover' }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </>
                )}

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isResubmitted}
                            onChange={(e) => setIsResubmitted(e.target.checked)}
                        />
                    }
                    label="This is a resubmission"
                />

                <Box display="flex" alignItems="center" justifyContent="flex-end" gap="15px">
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        size="large"
                        disabled={submitting || toVisit.uploading || fromVisit.uploading}
                    >
                        {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => props?.setOpen(false)}
                        size="large"
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default RequestCookConveyanceDialog;
